import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, dashboardPath } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { LogOut } from "lucide-react";

export function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500" data-testid="auth-loading">
        Chargement…
      </div>
    );
  if (!user) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={dashboardPath(user.role)} replace />;
  return children;
}

export function DashboardShell({ title, nav, active, children, accent = "#1D9BF0" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-20 md:pb-0">
      <div className="flex">
        {/* Sidebar desktop */}
        <aside className="fixed hidden h-screen w-[260px] flex-col border-r border-slate-200 bg-white p-5 md:flex">
          <Logo size="h-7 sm:h-8" />
          <div className="mt-8 flex-1 space-y-1">
            {nav.map((n) => {
              const isActive = active ? active === n.key : location.pathname === n.to;
              return (
                <button
                  key={n.key}
                  data-testid={`sidebar-${n.key}`}
                  onClick={() => (n.onClick ? n.onClick() : navigate(n.to))}
                  className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? "bg-[#263871] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <n.icon className="h-5 w-5" strokeWidth={1.6} />
                  {n.label}
                </button>
              );
            })}
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
            <button data-testid="logout-btn" onClick={onLogout} className="mt-3 flex items-center gap-2 text-xs font-medium text-red-500 hover:text-red-600">
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 md:ml-[260px]">
          <header className="cl-glass sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-8">
            <h1 className="text-xl font-bold text-[#263871] sm:text-2xl">{title}</h1>
            <div className="flex items-center gap-2">
              <NotificationBell accent={accent} />
              <Link to="/" className="text-sm text-slate-500 hover:text-[#263871]">Accueil</Link>
            </div>
          </header>
          <div className="mx-auto max-w-6xl animate-float-up p-4 sm:p-8">{children}</div>
        </div>
      </div>

      {/* Bottom nav mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-slate-200 bg-white py-2 md:hidden">
        {nav.slice(0, 5).map((n) => {
          const isActive = active ? active === n.key : location.pathname === n.to;
          return (
            <button
              key={n.key}
              data-testid={`bottomnav-${n.key}`}
              onClick={() => (n.onClick ? n.onClick() : navigate(n.to))}
              className="flex min-w-[60px] flex-col items-center gap-1 py-1 text-[10px] font-medium"
              style={{ color: isActive ? accent : "#94A3B8" }}
            >
              <n.icon className="h-5 w-5" strokeWidth={1.8} />
              {n.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
